import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Grid,
  Stack,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Autocomplete,
  Checkbox,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Badge,
  Fab,
  Collapse
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
  createNotification,
  getAdminNotifications,
  updateNotification,
  deleteNotification,
  getNotificationStats,
  getUsers
} from '../../services/notificationService';

const NotificationManagement = ({ isMobile }) => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'medium',
    targetUsers: 'all',
    specificUsers: [],
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    isSticky: false,
    showOnPages: ['all'],
    actionUrl: '',
    actionText: ''
  });

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notificationsResult, usersResult, statsResult] = await Promise.all([
        getAdminNotifications({
          page: page + 1,
          limit: rowsPerPage,
          ...filters
        }),
        getUsers(),
        getNotificationStats()
      ]);

      if (notificationsResult.success) {
        setNotifications(notificationsResult.data);
      }

      if (usersResult.success) {
        setUsers(usersResult.data);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      showSnackbar('Có lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateNew = () => {
    setEditingNotification(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      priority: 'medium',
      targetUsers: 'all',
      specificUsers: [],
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      isSticky: false,
      showOnPages: ['all'],
      actionUrl: '',
      actionText: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      priority: notification.priority,
      targetUsers: notification.targetUsers,
      specificUsers: notification.specificUsers || [],
      startDate: new Date(notification.startDate),
      endDate: new Date(notification.endDate),
      isSticky: notification.isSticky,
      showOnPages: notification.showOnPages || ['all'],
      actionUrl: notification.actionUrl || '',
      actionText: notification.actionText || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title.trim() || !formData.message.trim()) {
        showSnackbar('Vui lòng nhập đầy đủ tiêu đề và nội dung', 'error');
        return;
      }

      if (formData.endDate <= formData.startDate) {
        showSnackbar('Thời gian kết thúc phải sau thời gian bắt đầu', 'error');
        return;
      }

      if (formData.targetUsers === 'specific' && formData.specificUsers.length === 0) {
        showSnackbar('Vui lòng chọn ít nhất một người dùng', 'error');
        return;
      }

      const submitData = {
        ...formData,
        specificUsers: formData.targetUsers === 'specific' ? formData.specificUsers.map(u => u._id || u) : []
      };

      let result;
      if (editingNotification) {
        result = await updateNotification(editingNotification._id, submitData);
      } else {
        result = await createNotification(submitData);
      }

      if (result.success) {
        showSnackbar(
          editingNotification ? 'Cập nhật thông báo thành công' : 'Tạo thông báo thành công',
          'success'
        );
        setDialogOpen(false);
        fetchData();
      } else {
        showSnackbar(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (error) {
      showSnackbar('Có lỗi xảy ra khi lưu thông báo', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa thông báo này?')) return;

    try {
      const result = await deleteNotification(id);
      if (result.success) {
        showSnackbar('Xóa thông báo thành công', 'success');
        fetchData();
      } else {
        showSnackbar(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (error) {
      showSnackbar('Có lỗi xảy ra khi xóa thông báo', 'error');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'announcement':
        return <CampaignIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'announcement':
        return 'secondary';
      default:
        return 'info';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      default:
        return '#9e9e9e';
    }
  };

  const getStatusChip = (notification) => {
    const now = new Date();
    const startDate = new Date(notification.startDate);
    const endDate = new Date(notification.endDate);

    if (!notification.isActive) {
      return <Chip label="Tạm dừng" color="default" size="small" />;
    }

    if (now < startDate) {
      return <Chip label="Chờ hiển thị" color="warning" size="small" />;
    }

    if (now > endDate) {
      return <Chip label="Đã hết hạn" color="error" size="small" />;
    }

    return <Chip label="Đang hiển thị" color="success" size="small" />;
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    const cards = [
      {
        title: 'Tổng thông báo',
        value: stats.total,
        icon: <NotificationsIcon />,
        color: '#2196f3'
      },
      {
        title: 'Đang hoạt động',
        value: stats.active,
        icon: <CheckCircleIcon />,
        color: '#4caf50'
      },
      {
        title: 'Đã hết hạn',
        value: stats.expired,
        icon: <ScheduleIcon />,
        color: '#ff9800'
      },
      {
        title: 'Tổng lượt đọc',
        value: stats.totalReads,
        icon: <TrendingUpIcon />,
        color: '#9c27b0'
      }
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Card
              sx={{
                p: 2,
                textAlign: 'center',
                borderLeft: `4px solid ${card.color}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Box sx={{ color: card.color, mb: 1 }}>
                {card.icon}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.title}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              📢 Quản lý thông báo
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tạo và quản lý thông báo cho người dùng
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Làm mới">
              <IconButton onClick={fetchData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bộ lọc">
              <IconButton onClick={() => setShowFilters(!showFilters)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Tạo mới
            </Button>
          </Stack>
        </Box>

        {/* Stats Cards */}
        {renderStatsCards()}

        {/* Filters */}
        <Collapse in={showFilters}>
          <Card sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Trạng thái"
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="active">Đang hoạt động</MenuItem>
                    <MenuItem value="inactive">Tạm dừng</MenuItem>
                    <MenuItem value="expired">Đã hết hạn</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Loại</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    label="Loại"
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="info">Thông tin</MenuItem>
                    <MenuItem value="success">Thành công</MenuItem>
                    <MenuItem value="warning">Cảnh báo</MenuItem>
                    <MenuItem value="error">Lỗi</MenuItem>
                    <MenuItem value="announcement">Thông báo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Độ ưu tiên</InputLabel>
                  <Select
                    value={filters.priority}
                    onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                    label="Độ ưu tiên"
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="low">Thấp</MenuItem>
                    <MenuItem value="medium">Trung bình</MenuItem>
                    <MenuItem value="high">Cao</MenuItem>
                    <MenuItem value="urgent">Khẩn cấp</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
        </Collapse>

        {/* Notifications Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Thông báo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Loại</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Độ ưu tiên</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Trạng thái</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Thời gian</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Lượt đọc</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    // Loading skeleton
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Skeleton variant="circular" width={24} height={24} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="80%" />
                              <Skeleton variant="text" width="60%" />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell><Skeleton variant="rectangular" width={60} height={24} /></TableCell>
                        <TableCell><Skeleton variant="rectangular" width={60} height={24} /></TableCell>
                        <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                        <TableCell><Skeleton variant="text" width="100%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="40px" /></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Skeleton variant="circular" width={32} height={32} />
                            <Skeleton variant="circular" width={32} height={32} />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : notifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          Chưa có thông báo nào
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications.map((notification) => (
                      <TableRow key={notification._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ color: getTypeColor(notification.type) === 'info' ? '#2196f3' : undefined }}>
                              {getTypeIcon(notification.type)}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {notification.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  mt: 0.5
                                }}
                              >
                                {notification.message}
                              </Typography>
                              {notification.targetUsers === 'specific' && (
                                <Chip
                                  label={`${notification.specificUsers?.length || 0} người`}
                                  size="small"
                                  sx={{ mt: 1, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getTypeIcon(notification.type)}
                            label={notification.type}
                            color={getTypeColor(notification.type)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={notification.priority}
                            size="small"
                            sx={{
                              bgcolor: getPriorityColor(notification.priority),
                              color: 'white',
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {getStatusChip(notification)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(notification.startDate).toLocaleDateString('vi-VN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            đến {new Date(notification.endDate).toLocaleDateString('vi-VN')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Badge badgeContent={notification.readCount} color="primary">
                            <VisibilityIcon color="action" />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Chỉnh sửa">
                              <IconButton size="small" onClick={() => handleEdit(notification)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Xóa">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(notification._id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={stats?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Số hàng mỗi trang:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} của ${count !== -1 ? count : `hơn ${to}`}`
              }
            />
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              <Typography variant="h6">
                {editingNotification ? 'Chỉnh sửa thông báo' : 'Tạo thông báo mới'}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tiêu đề"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  variant="outlined"
                  placeholder="Nhập tiêu đề thông báo..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nội dung"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Nhập nội dung thông báo..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Loại thông báo</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Loại thông báo"
                  >
                    <MenuItem value="info">📢 Thông tin</MenuItem>
                    <MenuItem value="success">✅ Thành công</MenuItem>
                    <MenuItem value="warning">⚠️ Cảnh báo</MenuItem>
                    <MenuItem value="error">❌ Lỗi</MenuItem>
                    <MenuItem value="announcement">📣 Thông báo quan trọng</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Độ ưu tiên</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    label="Độ ưu tiên"
                  >
                    <MenuItem value="low">🔵 Thấp</MenuItem>
                    <MenuItem value="medium">🟡 Trung bình</MenuItem>
                    <MenuItem value="high">🟠 Cao</MenuItem>
                    <MenuItem value="urgent">🔴 Khẩn cấp</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Đối tượng</InputLabel>
                  <Select
                    value={formData.targetUsers}
                    onChange={(e) => setFormData({ ...formData, targetUsers: e.target.value })}
                    label="Đối tượng"
                  >
                    <MenuItem value="all">👥 Tất cả người dùng</MenuItem>
                    <MenuItem value="specific">👤 Người dùng cụ thể</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.targetUsers === 'specific' && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={users}
                    getOptionLabel={(option) => `${option.fullName} (${option.username})`}
                    value={formData.specificUsers}
                    onChange={(e, newValue) => setFormData({ ...formData, specificUsers: newValue })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Chọn người dùng"
                        placeholder="Tìm và chọn người dùng..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.fullName}
                          {...getTagProps({ index })}
                          key={option._id}
                        />
                      ))
                    }
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Thời gian bắt đầu"
                  value={formData.startDate}
                  onChange={(newValue) => setFormData({ ...formData, startDate: newValue })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Thời gian kết thúc"
                  value={formData.endDate}
                  onChange={(newValue) => setFormData({ ...formData, endDate: newValue })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Hiển thị trên trang</InputLabel>
                  <Select
                    multiple
                    value={formData.showOnPages}
                    onChange={(e) => setFormData({ ...formData, showOnPages: e.target.value })}
                    label="Hiển thị trên trang"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="all">Tất cả trang</MenuItem>
                    <MenuItem value="dashboard">Dashboard</MenuItem>
                    <MenuItem value="scheduler">Lịch đăng ký</MenuItem>
                    <MenuItem value="history">Lịch sử</MenuItem>
                    <MenuItem value="reports">Báo cáo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="URL hành động (tùy chọn)"
                  value={formData.actionUrl}
                  onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                  placeholder="https://..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Text nút hành động (tùy chọn)"
                  value={formData.actionText}
                  onChange={(e) => setFormData({ ...formData, actionText: e.target.value })}
                  placeholder="Xem chi tiết"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSticky}
                      onChange={(e) => setFormData({ ...formData, isSticky: e.target.checked })}
                    />
                  }
                  label="Thông báo dính (không tự ẩn)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setDialogOpen(false)}>
              Hủy
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              startIcon={<SendIcon />}
              sx={{ 
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                }
              }}
            >
              {editingNotification ? 'Cập nhật' : 'Tạo thông báo'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Floating Action Button for mobile */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleCreateNew}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              background: 'linear-gradient(45deg, #667eea, #764ba2)'
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default NotificationManagement; 